import React, { FC, useCallback, useEffect, useState } from 'react';
import { useKeyboardShortcut } from 'src/hooks';
import {
  LearningSessionAction,
  ResponseState,
} from 'src/apps/LearningSession/hooks/learningSessionReducer';
import {
  TappingOption,
  TappingOptionButton,
  TappingOptionButtonState,
  TappingOptionButtonWithRef,
} from './TappingOptionButton';
import * as S from './styles';
import { TappingCopyOption } from './TappingCopyOption';
import { Direction, generateOptionsGrid, getNextOptionToFocus } from './utils';

interface TappingResponseProps {
  tappingOptions: string[];
  responseState: ResponseState;
  learningSessionDispatch: (action: LearningSessionAction) => void;
  answers?: string[];
  showBigger?: boolean;
  isCopy?: boolean;
  showCorrectAnswer?: boolean;
  onVideo?: boolean;
}

export const responseStateToButtonState = (
  responseState: ResponseState,
): TappingOptionButtonState => {
  const lookup: Record<ResponseState, TappingOptionButtonState> = {
    Unanswered: 'default',
    Selected: 'yellow',
    Processing: 'yellow',
    Correct: 'green',
    Incorrect: 'red',
    NearlyCorrect: 'orange',
  };
  return lookup[responseState];
};

/**
 * @example
 * convertIndexToDisplay(0, 2); // 01
 */
export const convertIndexToDisplay = (index: number, paddingWidth: number): string =>
  (index + 1).toString().padStart(paddingWidth, '0');

/**
 * Mousetrap requires a space between keys in sequence so 01 is '0 1'
 *
 * @example
 * convertFromDisplayLabelToKeyShortcut('01'); // '0 1'
 */
const convertFromDisplayLabelToKeyShortcut = (displayLabel: string): string =>
  displayLabel.split('').join(' ');

/**
 * @example
 * convertFromKeyShortcutToDisplayLabel('0 1'); // '01'
 */
const convertFromKeyShortcutToDisplayLabel = (key: string): string => key.replace(' ', '');

const getPaddingWidth = (numberOptions: number, showIndex: boolean): number => {
  if (!showIndex || numberOptions <= 9) return 0;
  return 2;
};

export const getShortcuts = (numberOptions: number, showIndex: boolean) => {
  let numberShortcuts = numberOptions;

  if (!showIndex) {
    numberShortcuts = 9; // User can press any number key to display options
  }

  const padding = getPaddingWidth(numberOptions, showIndex);

  return Array.from({ length: numberShortcuts }, (_v, i) =>
    convertFromDisplayLabelToKeyShortcut(convertIndexToDisplay(i, padding)),
  );
};

const shouldAutosubmit = (stringAnswer: string, answers?: string[]) =>
  answers && stringAnswer === answers.join(' ');

export const TappingResponse: FC<TappingResponseProps> = ({
  tappingOptions,
  responseState,
  learningSessionDispatch,
  showBigger,
  isCopy,
  answers,
  showCorrectAnswer,
  onVideo = false,
}) => {
  const [options, setOptions] = useState<TappingOption[]>([]);
  const [answer, setAnswer] = useState<TappingOption[]>([]);
  const [showIndex, setShowIndex] = useState(false);
  const [currentlyFocusedOptionGridIndex, setCurrentlyFocusedOptionGridIndex] = useState<
    [number, number] | null
  >(null);
  const [optionsGrid, setOptionsGrid] = useState<number[][]>([]);

  // create a ref for each option
  const optionRefs = React.useMemo(
    () => options.map(() => React.createRef<HTMLButtonElement>()),
    [options],
  );

  const generateAndSetOptionsGrid = useCallback(() => {
    const newGrid = generateOptionsGrid(optionRefs);
    setOptionsGrid(newGrid);
  }, [optionRefs, setOptionsGrid]);

  // navigate between options with keyboard
  const focusNext = useCallback(
    (direction: Direction) => {
      const nextOption = getNextOptionToFocus(
        optionsGrid,
        currentlyFocusedOptionGridIndex,
        direction,
      );

      const elToFocus: React.RefObject<HTMLButtonElement | null> =
        optionRefs[nextOption.newOptionIndex];
      elToFocus?.current && elToFocus.current?.focus();

      setCurrentlyFocusedOptionGridIndex(nextOption.newOptionGridIndex);
    },
    [currentlyFocusedOptionGridIndex, optionRefs, optionsGrid],
  );

  useKeyboardShortcut(['up', 'k'], () => focusNext('up'));
  useKeyboardShortcut(['right', 'l'], () => focusNext('right'));
  useKeyboardShortcut(['down', 'j'], () => focusNext('down'));
  useKeyboardShortcut(['left', 'h'], () => focusNext('left'));

  const shortcuts = getShortcuts(tappingOptions.length, showIndex);

  // Reset answer when choices change
  useEffect(() => {
    setAnswer([]);
  }, [setAnswer, tappingOptions]);

  // Send partial answers to session
  const calculateAndSetUserAnswer = useCallback(
    (newAnswer: TappingOption[], newOptions: TappingOption[]) => {
      setAnswer(newAnswer);
      const stringAnswer = newAnswer
        .map((singleAnswer: TappingOption) => {
          return singleAnswer.value;
        })
        .join(' ');
      if (shouldAutosubmit(stringAnswer, answers)) {
        learningSessionDispatch({
          type: 'ANSWER_TEST',
          answer: stringAnswer,
        });
      } else {
        learningSessionDispatch({
          type: 'TENTATIVELY_SELECT_ANSWER',
          answer: stringAnswer,
        });
      }
      setOptions(newOptions);
    },
    [setAnswer, setOptions, learningSessionDispatch, answers],
  );

  const handleAddClick = (index: number) => () => {
    const clickedOption = options[index];
    // if current option is not used
    if (!clickedOption.used) {
      // XXX evaluate responseState here and autosubmit call handleAnswer from userAnswerHandlers

      const updatedOption = {
        ...clickedOption,
        used: true,
      };

      const newOptions = options.map((option, idx) => {
        if (idx === index) return updatedOption;

        return option;
      });

      const newAnswer = [...answer, updatedOption];
      calculateAndSetUserAnswer(newAnswer, newOptions);
    }
  };

  const handleRemoveClick = (index: number) => () => {
    const newAnswer = answer.filter(a => a.index !== index);
    const newOptions = options.map((option, i) => {
      if (index === i) {
        return {
          ...option,
          used: false,
        };
      }

      return option;
    });

    calculateAndSetUserAnswer(newAnswer, newOptions);
  };

  const handleClear = () => {
    /* istanbul ignore if: clear button is disabled when answer is empty */
    if (answer.length === 0) return;

    setAnswer([]);
    setOptions(
      options.map(option => ({
        ...option,
        used: false,
      })),
    );
    learningSessionDispatch({ type: 'RESET_ANSWER' });
  };

  const toggleSelectedOption = (key: string) => {
    const keyAsNumber = parseInt(convertFromKeyShortcutToDisplayLabel(key));

    if (!showIndex) {
      setShowIndex(true);
    } else {
      const index = keyAsNumber - 1;
      const isOptionSelected = answer.reduce((acc, curr) => {
        if (curr.index === index) return true;

        return acc;
      }, false);

      if (isOptionSelected) {
        // because currying is used for the onClick,
        // we need to immediately invoke returned function
        handleRemoveClick(index)();
      } else {
        handleAddClick(index)();
      }
    }
  };

  useKeyboardShortcut(shortcuts, (_, combo) => {
    toggleSelectedOption(combo);
  });

  useKeyboardShortcut('backspace', () => {
    if (answer.length === 0) return;

    handleRemoveClick(answer[answer.length - 1].index)();
  });

  useEffect(() => {
    const options = tappingOptions.map((value, index) => ({
      index,
      value,
      responseState: undefined,
      used: false,
    }));

    setOptions(options);
  }, [tappingOptions]);

  const getLabel = (index: number) =>
    showIndex
      ? convertIndexToDisplay(index, getPaddingWidth(tappingOptions.length, showIndex))
      : null;
  const showHint = isCopy || showCorrectAnswer;

  return (
    <S.Container dir="auto">
      {showHint && answers && (
        <S.OptionContainer data-testid="tapping-copy-options" $ruleBelow>
          {answers.map((copyWord, index) => (
            <TappingCopyOption
              key={`${copyWord}-${index}`} // eslint-disable-line react/no-array-index-key
              value={copyWord}
              data-testid="tapping-copy-option"
              showBigger={!!showBigger}
            />
          ))}
        </S.OptionContainer>
      )}

      <S.OptionContainer
        data-testid="tapping-response-answer"
        $ruleBelow={!showHint}
        $absolute={showHint}
      >
        {answer?.length > 0 &&
          answer?.map(({ index, value }) => (
            <TappingOptionButton
              key={`${value}-${index}-selected`}
              index={index}
              value={value}
              buttonState={responseStateToButtonState(responseState)}
              used={false}
              onClick={handleRemoveClick(index)}
              label={getLabel(index)}
              data-testid="tapping-response-selected-option"
              showBigger={!!showBigger}
              onVideo={onVideo}
            />
          ))}
      </S.OptionContainer>

      <S.OptionsRow>
        <S.OptionsBank $ruleBelow={false}>
          {options.map(({ index, value, used }) => (
            <TappingOptionButtonWithRef
              key={`${value}-${index}-option`}
              ref={optionRefs[index]}
              {...{ index, value, used }}
              buttonState="default"
              label={getLabel(index)}
              onClick={handleAddClick(index)}
              data-testid="tapping-response-option"
              showBigger={!!showBigger}
              onVideo={onVideo}
              generateAndSetOptionsGrid={generateAndSetOptionsGrid}
              isLastOption={index === options.length - 1}
              onBlur={() => {
                if (currentlyFocusedOptionGridIndex) {
                  const currentOptionIndex =
                    optionsGrid[currentlyFocusedOptionGridIndex[0]][
                      currentlyFocusedOptionGridIndex[1]
                    ];
                  optionRefs[currentOptionIndex].current?.blur();
                }
                setCurrentlyFocusedOptionGridIndex(null);
              }}
            />
          ))}
        </S.OptionsBank>
        <S.ClearButtonContainer>
          <S.ClearButton
            type="button"
            onClick={handleClear}
            disabled={answer.length === 0 || !['Unanswered', 'Selected'].includes(responseState)}
            data-testid="tapping-response-clear"
            aria-label="Clear"
          >
            <S.ClearIcon name="close" />
          </S.ClearButton>
        </S.ClearButtonContainer>
      </S.OptionsRow>
    </S.Container>
  );
};
